import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

interface WishlistContextType {
  count: number;
  wishlistIds: Set<string>;
  isWishlisted: (artworkId: string) => boolean;
  toggleWishlist: (artworkId: string) => Promise<boolean>;
  refreshWishlist: () => Promise<void>;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlistIds(new Set());
      return;
    }
    try {
      const response = await api.get('/wishlist');
      const items = response.data?.items || [];
      const ids = new Set<string>(items.map((item: any) => item.artworkId));
      setWishlistIds(ids);
    } catch (error) {
      console.error('Failed to fetch wishlist count:', error);
    }
  }, [user]);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  const isWishlisted = useCallback((artworkId: string) => {
    return wishlistIds.has(artworkId);
  }, [wishlistIds]);

  const toggleWishlist = useCallback(async (artworkId: string): Promise<boolean> => {
    if (!user) return false;

    const currentlyWishlisted = wishlistIds.has(artworkId);
    
    // Optimistic state update
    setWishlistIds((prev) => {
      const updated = new Set(prev);
      if (currentlyWishlisted) {
        updated.delete(artworkId);
      } else {
        updated.add(artworkId);
      }
      return updated;
    });

    setLoading(true);
    try {
      if (currentlyWishlisted) {
        await api.delete(`/wishlist/${artworkId}`);
        toast.success('Removed from wishlist');
        return false;
      } else {
        await api.post(`/wishlist/${artworkId}`);
        toast.success('Saved to wishlist');
        return true;
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      // Rollback optimistic update
      setWishlistIds((prev) => {
        const rollback = new Set(prev);
        if (currentlyWishlisted) {
          rollback.add(artworkId);
        } else {
          rollback.delete(artworkId);
        }
        return rollback;
      });
      toast.error('Failed to update wishlist');
      return currentlyWishlisted;
    } finally {
      setLoading(false);
    }
  }, [user, wishlistIds]);

  return (
    <WishlistContext.Provider
      value={{
        count: wishlistIds.size,
        wishlistIds,
        isWishlisted,
        toggleWishlist,
        refreshWishlist,
        loading,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

const defaultWishlistContext: WishlistContextType = {
  count: 0,
  wishlistIds: new Set(),
  isWishlisted: () => false,
  toggleWishlist: async () => false,
  refreshWishlist: async () => {},
  loading: false,
};

export const useWishlistContext = () => {
  const context = useContext(WishlistContext);
  return context || defaultWishlistContext;
};
